import React, { useEffect, useState } from 'react';
import { getDriverApp } from '../../truckmateStore';
import LoadSheetBox from './LoadSheetBox';
import { ProfileForm } from './DriverProfiles';

// One read for every card on the board, shared and refreshed every minute while the board is open
const EVERY = 60000;
const shared = { at: 0, trips: null, wait: null, subs: new Set() };
function refresh(force = false) {
  if (shared.wait || (!force && shared.trips && Date.now() - shared.at < EVERY)) return shared.wait;
  shared.wait = Promise.resolve().then(() => getDriverApp()).then((r) => { shared.trips = (r && r.trips) || {}; shared.at = Date.now(); shared.subs.forEach((f) => f()); })
    .catch(() => {}).finally(() => { shared.wait = null; });
  return shared.wait;
}
export function useDriverApp(trip) {
  const [, tick] = useState(0);
  useEffect(() => {
    const f = () => tick((n) => n + 1);
    shared.subs.add(f); refresh();
    const t = setInterval(() => refresh(), EVERY);
    return () => { shared.subs.delete(f); clearInterval(t); };
  }, []);
  return shared.trips ? shared.trips[String(trip)] || null : undefined;
}

const ago = (v) => {
  const ms = Date.now() - Date.parse(v); if (!Number.isFinite(ms)) return '';
  const m = Math.round(ms / 60000);
  return m < 2 ? 'just now' : m < 60 ? `${m} min ago` : m < 48 * 60 ? `${Math.round(m / 60)} h ago` : `${Math.round(m / 1440)} days ago`;
};
const when = (v) => { const d = new Date(v); return Number.isNaN(d.getTime()) ? '' : d.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }); };

// The collapsed line on the card: the one thing dispatch needs to know first. Pure.
export function driverAppSummary(v) {
  if (v === undefined) return 'Checking…';
  if (!v || !v.drivers.length) return 'No driver on the load in TruckMate';
  if (v.drivers.every((d) => !d.onList)) return 'Driver can’t sign in: no driver profile yet';
  if (!v.drivers.some((d) => d.signedIn)) return 'Driver not signed in to the app yet';
  if (v.confirmed) return `Driver confirmed the load · ${when(v.confirmed.at)}`;
  if (v.opened) return `Driver opened the load · ${when(v.opened.at)}`;
  if (v.sent) return `Load sent · ${when(v.sent.at)} · not opened yet`;
  return 'Signed in · load info not sent';
}
// Needs attention: a driver who can't get in, or a load sent and not opened for an hour. Pure.
export function driverAppTone(v, now = Date.now()) {
  if (!v || !v.drivers.length) return null;
  if (v.drivers.every((d) => !d.onList) || !v.drivers.some((d) => d.signedIn)) return 'warning';
  if (v.sent && !v.opened && !v.confirmed && now - Date.parse(v.sent.at) > 3600000) return 'warning';
  return null;
}

const Chip = ({ ok, children }) => <span className="tmw-chip" data-tone={ok ? 'ok' : 'warning'} style={{ marginRight: 6 }}>{children}</span>;
const HOW = 'Sign in to TagAlong at mytagalong.app/driver (or the TagAlong app): your driver ID and your cell number, then make a 4-digit PIN. Allow notifications so you get your loads and dispatch messages.';

// Everything the driver app has on this load, in order: can the driver get in, did they get the load, did they open it, did they confirm
export default function DriverAppBox({ trip, onChanged, onError }) {
  const v = useDriverApp(trip.tripNumber);
  const [copied, setCopied] = useState(false);
  const [making, setMaking] = useState(null);   // the driver ID dispatch is making a profile for
  const copy = async () => { try { await navigator.clipboard.writeText(HOW); setCopied(true); setTimeout(() => setCopied(false), 2000); } catch { /* the text is on screen */ } };
  const changed = () => { refresh(true); if (onChanged) onChanged(); };
  const steps = v ? [
    ['Load sent', v.sent ? `${v.sent.by ? `by ${v.sent.by} · ` : ''}${when(v.sent.at)}` : null],
    ['Notification', v.pushed ? `${v.pushed.result} · ${when(v.pushed.at)}` : null, v.pushed && v.pushed.result !== 'sent'],
    ['Opened', v.opened ? when(v.opened.at) : null],
    ['Confirmed', v.confirmed ? when(v.confirmed.at) : null],
  ] : [];
  return (
    <div aria-label={`Driver app for trip ${trip.tripNumber}`}>
      {v === undefined && <div className="tmw-small">Checking the driver app…</div>}
      {v && v.drivers.length === 0 && <div className="tmw-note">No driver is on this load in TruckMate yet. Once one is, the load shows in their app.</div>}
      {v && v.drivers.map((d) => (
        <div key={d.code} className="tmw-small" style={{ marginBottom: 8 }}>
          <b>{d.name || 'Driver'}</b> · ID {d.code}
          <div style={{ marginTop: 4 }}>
            {!d.onList ? <Chip>No driver profile</Chip> : !d.hasCell ? <Chip>No cell on file</Chip> : <Chip ok>Has a profile</Chip>}
            {d.signedIn ? <Chip ok>Signed in</Chip> : <Chip>Not signed in yet</Chip>}
            {d.signedIn && (d.notifications ? <Chip ok>Notifications on</Chip> : <Chip>Notifications off</Chip>)}
            {d.lastSeenAt && <span className="tmw-small">Last in the app {ago(d.lastSeenAt)}</span>}
          </div>
          {!d.onList && making !== d.code && <div className="tmw-note" style={{ marginTop: 4 }}>They can’t sign in yet: driver ID {d.code} has no profile. <button type="button" className="tmw-link" onClick={() => setMaking(d.code)}>Make their profile</button> (name and cell) and they sign in like any driver.</div>}
          {!d.onList && making === d.code && <div style={{ marginTop: 6 }}><ProfileForm driver={{ code: d.code, name: d.name, phones: [] }} onSaved={() => { setMaking(null); changed(); }} onCancel={() => setMaking(null)} /></div>}
          {d.onList && !d.signedIn && <div className="tmw-note" style={{ marginTop: 4 }}>Send them the sign-in steps: <button type="button" className="tmw-link" onClick={copy}>{copied ? 'Copied' : 'Copy the steps'}</button></div>}
          {d.signedIn && !d.notifications && <div className="tmw-note" style={{ marginTop: 4 }}>Their phone isn’t getting notifications: ask them to open the TagAlong app and allow notifications.</div>}
        </div>
      ))}
      {v && v.drivers.length > 0 && (
        <ol className="tmw-small" aria-label="The load on the driver's phone" style={{ display: 'flex', flexWrap: 'wrap', gap: 6, listStyle: 'none', padding: 0, margin: '4px 0 8px' }}>
          {steps.map(([label, val, warn]) => (
            <li key={label} className="tmw-chip" data-tone={val ? (warn ? 'warning' : 'ok') : undefined}>{val ? '✓' : '○'} {label}{val ? ` · ${val}` : ''}</li>
          ))}
        </ol>
      )}
      <LoadSheetBox trip={trip} onChanged={changed} onError={onError} />
    </div>
  );
}
